import { api } from '../../lib/api.js';
import { BaseComponent, html, nothing, renderMarkdown } from '../../lib/lit.js';
import { MARKDOWN_STYLES } from '../../lib/markdown-styles.js';
import './community-markdown-input.js';
import { router } from '../../lib/router.js';
import { formatItemDate, itemTypeMeta } from './community-item-list.js';

/**
 * <community-item-page> — one feedback item and its flat thread.
 *
 * This is the anchor for stage 2: the GitHub issue will carry a link back to
 * this URL, so `/items/:id` is effectively permanent from the moment the first
 * issue is filed.
 *
 * A comment's `origin` is the only thing distinguishing a team reply relayed
 * from GitHub from another playtester's comment — both render as a handle — so
 * it is surfaced rather than dropped.
 */
/**
 * Triage vocabularies, mirroring the dice-driven org's own GitHub issue fields
 * so a portal item and the issue behind it read the same. Stored lowercase; the
 * label is what a person sees.
 *
 * Severity describes how much something HURT the person who hit it, which is why
 * the words are experiential rather than technical.
 */
export const PRIORITIES = [
    { value: 'urgent', label: 'Urgent', badge: 'text-bg-danger' },
    { value: 'high', label: 'High', badge: 'text-bg-warning' },
    { value: 'medium', label: 'Medium', badge: 'text-bg-secondary' },
    { value: 'low', label: 'Low', badge: 'text-bg-light border' },
];

export const SEVERITIES = [
    { value: 'blocker', label: 'Blocker', badge: 'text-bg-danger' },
    { value: 'frustration', label: 'Frustration', badge: 'text-bg-warning' },
    { value: 'annoyance', label: 'Annoyance', badge: 'text-bg-secondary' },
    { value: 'nit', label: 'Nit', badge: 'text-bg-light border' },
];

export class CommunityItemPage extends BaseComponent {
    /** Markdown bodies render into `.markdown-body`; see markdown-styles.js. */
    static styles = MARKDOWN_STYLES;

    static properties = {
        itemId: { type: String },
        isStaff: { type: Boolean },
        products: { type: Array },
        _item: { state: true },
        _comments: { state: true },
        _loading: { state: true },
        _error: { state: true },
        _draft: { state: true },
        _sending: { state: true },
        _canEdit: { state: true },
        _editing: { state: true },
        _editTitle: { state: true },
        _editBody: { state: true },
        _saving: { state: true },
        _uploadingEdit: { state: true },
        _uploadingComment: { state: true },
        _triagePriority: { state: true },
        _triageSeverity: { state: true },
        _githubUrl: { state: true },
        _settingState: { state: true },
        _following: { state: true },
        _settingFollow: { state: true },
    };

    constructor() {
        super();
        this.itemId = '';
        this.isStaff = false;
        /** @type {Array<{slug: string, name: string}>} */
        this.products = [];
        /** @type {object|null} */
        this._item = null;
        /** @type {Array<object>} */
        this._comments = [];
        this._loading = true;
        this._error = '';
        this._draft = '';
        this._sending = false;
        this._settingState = false;
        this._canEdit = false;
        this._editing = false;
        this._editTitle = '';
        this._editBody = '';
        this._saving = false;
        this._uploadingEdit = false;
        this._uploadingComment = false;
        this._triagePriority = '';
        this._triageSeverity = '';
        this._githubUrl = '';
        this._following = false;
        this._settingFollow = false;
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    /**
     * Open or close the item. Staff only, and portal-side only: the GitHub
     * issue keeps its own state and is deliberately not touched.
     * @param {string} state
     */
    async _setState(state) {
        this._settingState = true;
        this._error = '';
        try {
            const updated = await api.patch(`/staff/items/${this.itemId}/state`, { state });
            this._item = updated;
        } catch (err) {
            this._error = String(err.message ?? err);
        } finally {
            this._settingState = false;
        }
    }

    /**
     * Follow or stop following: whether you hear about replies, closes and
     * changelog credits for this item. Anyone can follow anything; filing and
     * commenting follow automatically.
     * @param {boolean} following
     */
    async _setFollowing(following) {
        this._settingFollow = true;
        this._error = '';
        try {
            if (following) {
                await api.put(`/items/${this.itemId}/follow`);
            } else {
                await api.delete(`/items/${this.itemId}/follow`);
            }
            this._following = following;
        } catch (err) {
            this._error = String(err.message ?? err);
        } finally {
            this._settingFollow = false;
        }
    }

    /**
     * Archive or unarchive the item. Staff only, and only a closed item can be
     * archived — the API refuses otherwise. Archiving takes the item off the
     * community feed and "your activity"; this page still loads it by URL.
     * @param {boolean} archived
     */
    _startEditing() {
        this._editTitle = this._item?.title ?? '';
        this._editBody = this._item?.body ?? '';
        this._error = '';
        this._editing = true;
    }

    /**
     * Save the rewritten item. Author-only, enforced by the API; the button is
     * only rendered when the server said canEdit.
     *
     * Portal-side only — the backing GitHub issue keeps its own title and body
     * and is deliberately not updated.
     */
    async _saveEdit() {
        const title = this._editTitle.trim();
        if (!title) return;
        this._saving = true;
        this._error = '';
        try {
            const updated = await api.patch(`/items/${this.itemId}`, {
                title,
                body: this._editBody.trim(),
            });
            this._item = updated;
            this._editing = false;
        } catch (err) {
            this._error = String(err.message ?? err);
        } finally {
            this._saving = false;
        }
    }

    /**
     * One triage badge, or nothing when the field is unset. Untriaged is the
     * resting state for most items, so an "unset" badge would be noise on
     * nearly every page.
     * @param {Array<{value: string, label: string, badge: string}>} vocabulary
     * @param {string|undefined} value
     * @param {string} title
     */
    _renderTriageBadge(vocabulary, value, title) {
        const entry = vocabulary.find((v) => v.value === value);
        if (!entry) return nothing;
        return html`<span class="badge ${entry.badge}" title=${title}>${entry.label}</span>`;
    }

    /** `owner/repo#number`, read back from the URL the server built. */
    _issueLabel() {
        const m = /github\.com\/([^/]+\/[^/]+)\/issues\/(\d+)/.exec(this._githubUrl);
        return m ? `${m[1]}#${m[2]}` : 'GitHub issue';
    }

    /** Save priority and severity together — triage is one act. */
    async _saveTriage() {
        this._settingState = true;
        this._error = '';
        try {
            const updated = await api.patch(`/staff/items/${this.itemId}/triage`, {
                priority: this._triagePriority,
                severity: this._triageSeverity,
            });
            this._item = updated;
        } catch (err) {
            this._error = String(err.message ?? err);
        } finally {
            this._settingState = false;
        }
    }

    /**
     * Assign the item's product. Staff only, and the one control here with an
     * outbound consequence: an item filed without a product was never sent to
     * GitHub, so naming one is what files the issue.
     * @param {string} product
     */
    async _setProduct(product) {
        if (!product) return;
        this._settingState = true;
        this._error = '';
        try {
            const updated = await api.patch(`/staff/items/${this.itemId}/product`, { product });
            this._item = updated;
        } catch (err) {
            this._error = String(err.message ?? err);
        } finally {
            this._settingState = false;
        }
    }

    async _setArchived(archived) {
        this._settingState = true;
        this._error = '';
        try {
            const updated = await api.patch(`/staff/items/${this.itemId}/archived`, { archived });
            this._item = updated;
        } catch (err) {
            this._error = String(err.message ?? err);
        } finally {
            this._settingState = false;
        }
    }

    updated(changed) {
        if (changed.has('itemId') && changed.get('itemId') !== undefined) this._load();
    }

    async _load() {
        if (!this.itemId) return;
        this._loading = true;
        this._error = '';
        try {
            const result = await api.get(`/items/${this.itemId}`);
            this._item = result.item;
            this._canEdit = result.canEdit === true;
            this._triagePriority = result.item?.priority ?? '';
            this._triageSeverity = result.item?.severity ?? '';
            // Staff-only, and only once relayed. The server decides — the SPA
            // never derives it, so a member cannot see one by reading the page.
            this._githubUrl = result.githubUrl ?? '';
            this._following = result.following === true;
            this._comments = result.comments ?? [];
        } catch (err) {
            // A 404 here is the product scope doing its job as much as it is a
            // genuinely missing item — the two are deliberately the same answer.
            this._error =
                /** @type {any} */ (err).status === 404
                    ? "That feedback item does not exist, or it belongs to a playtest you're not part of."
                    : String(err);
        } finally {
            this._loading = false;
        }
    }

    async _comment(event) {
        event.preventDefault();
        const body = this._draft.trim();
        if (!body || this._sending) return;

        this._sending = true;
        try {
            const comment = await api.post(`/items/${this.itemId}/comments`, { body });
            this._comments = [...this._comments, comment];
            this._draft = '';
            // Commenting follows the item on the server, even after an
            // unfollow; say so here rather than refetching to find out.
            this._following = true;
        } catch (err) {
            this._error = /** @type {any} */ (err).message || String(err);
        } finally {
            this._sending = false;
        }
    }

    /** The item as written, with an Edit affordance for its author. */
    _renderBody(item) {
        return html`
            <h1 class="h4 mb-2">${item.title}</h1>
            <div class="text-muted small mb-3 d-flex align-items-center gap-2 flex-wrap">
                <span>${item.authorHandle} · ${formatItemDate(item.createdAt)}</span>
                ${
                    this._canEdit
                        ? html`<button
                              class="btn btn-sm btn-link p-0 align-baseline"
                              @click=${() => this._startEditing()}
                          >
                              Edit
                          </button>`
                        : nothing
                }
            </div>
            ${
                item.body
                    ? html`<div class="markdown-body">${renderMarkdown(item.body)}</div>`
                    : html`<div class="text-muted fst-italic">No description.</div>`
            }
        `;
    }

    /**
     * Editing your own item. Markdown is supported, so the hint says so — a
     * report filed from Discord arrives as one line and this is where it gets
     * written up properly.
     */
    _renderEditor() {
        return html`
            <div class="mb-3">
                <label class="form-label" for="edit-title">Title</label>
                <input
                    id="edit-title"
                    class="form-control"
                    maxlength="200"
                    .value=${this._editTitle}
                    @input=${(e) => {
                        this._editTitle = e.target.value;
                    }}
                />
            </div>
            <div class="mb-3">
                <community-markdown-input
                    input-id="edit-body"
                    label="Details"
                    optional
                    rows="6"
                    .value=${this._editBody}
                    @value-changed=${(e) => {
                        this._editBody = e.detail.value;
                    }}
                    @uploading-changed=${(e) => {
                        this._uploadingEdit = e.detail.uploading;
                    }}
                ></community-markdown-input>
            </div>
            ${
                this._error
                    ? html`<div class="alert alert-danger py-2">${this._error}</div>`
                    : nothing
            }
            <div class="d-flex gap-2">
                <button
                    class="btn btn-primary"
                    ?disabled=${this._saving || this._uploadingEdit || this._editTitle.trim() === ''}
                    @click=${() => this._saveEdit()}
                >
                    ${this._saving ? 'Saving…' : this._uploadingEdit ? 'Waiting for the image…' : 'Save'}
                </button>
                <button
                    class="btn btn-outline-secondary"
                    ?disabled=${this._saving}
                    @click=${() => {
                        this._editing = false;
                        this._error = '';
                    }}
                >
                    Cancel
                </button>
            </div>
        `;
    }

    /**
     * Everything staff can set on an item, in one place. Members never see it.
     *
     * Product only appears while it is unset, because assigning it is what files
     * the GitHub issue and re-assigning afterwards does NOT move that issue —
     * offering a picker that silently does nothing would be worse than not
     * offering one.
     *
     * Priority and severity stay editable: they are our judgement about the
     * item, they change as we learn more, and nothing outside the portal reads
     * them.
     * @param {object} item
     */
    _renderStaffPanel(item) {
        const canPrioritise = item.type === 'bug' || item.type === 'feature';
        const canSetSeverity = item.type === 'bug';
        // The link alone is reason enough to show the panel: an idea or a general
        // remark carries neither priority nor severity, but staff still need a
        // way to reach its issue.
        if (!item.product && !canPrioritise && !canSetSeverity && !this._githubUrl) return nothing;

        return html`
            <div class="border-top mt-3 pt-3">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <span class="text-muted small">Staff</span>
                    ${
                        this._githubUrl
                            ? html`<a
                                  class="small"
                                  href=${this._githubUrl}
                                  target="_blank"
                                  rel="noopener"
                              >
                                  <i class="bi bi-github me-1"></i>${this._issueLabel()}
                              </a>`
                            : nothing
                    }
                </div>
                ${!item.product ? this._renderTriage() : nothing}
                ${
                    canPrioritise || canSetSeverity
                        ? html`<div class="d-flex gap-2 align-items-end flex-wrap">
                              ${
                                  canPrioritise
                                      ? this._renderTriageSelect(
                                            'priority',
                                            'Priority',
                                            PRIORITIES,
                                            this._triagePriority,
                                            (v) => {
                                                this._triagePriority = v;
                                            },
                                        )
                                      : nothing
                              }
                              ${
                                  canSetSeverity
                                      ? this._renderTriageSelect(
                                            'severity',
                                            'Severity',
                                            SEVERITIES,
                                            this._triageSeverity,
                                            (v) => {
                                                this._triageSeverity = v;
                                            },
                                        )
                                      : nothing
                              }
                              <button
                                  class="btn btn-sm btn-outline-secondary"
                                  ?disabled=${this._settingState}
                                  @click=${() => this._saveTriage()}
                              >
                                  Save
                              </button>
                          </div>`
                        : nothing
                }
            </div>
        `;
    }

    /**
     * One triage dropdown. "Not set" is a real choice, not a placeholder —
     * clearing a priority is something staff legitimately do.
     * @param {string} id
     * @param {string} label
     * @param {Array<{value: string, label: string}>} vocabulary
     * @param {string} selected
     * @param {(value: string) => void} onChange
     */
    _renderTriageSelect(id, label, vocabulary, selected, onChange) {
        return html`
            <div>
                <label class="form-label small text-muted mb-1" for="triage-${id}">${label}</label>
                <select
                    id="triage-${id}"
                    class="form-select form-select-sm"
                    ?disabled=${this._settingState}
                    .value=${selected}
                    @change=${(e) => onChange(e.target.value)}
                >
                    <option value="">Not set</option>
                    ${vocabulary.map(
                        (v) => html`<option value=${v.value} ?selected=${v.value === selected}>
                            ${v.label}
                        </option>`,
                    )}
                </select>
            </div>
        `;
    }

    /**
     * Triage: give an untriaged item a product. Shown only to staff, and only
     * while the item has none — once set, the item has a home and this is done.
     */
    _renderTriage() {
        return html`
            <div class="mb-3">
                <label class="form-label small text-muted" for="triage-product">
                    Not assigned to a product yet. Setting one files the GitHub issue.
                </label>
                <div class="d-flex gap-2" style="max-width: 24rem;">
                    <select
                        id="triage-product"
                        class="form-select form-select-sm"
                        ?disabled=${this._settingState}
                        @change=${(e) => {
                            this._triageProduct = e.target.value;
                        }}
                    >
                        <option value="">Choose a product…</option>
                        ${this.products.map(
                            (p) => html`<option value=${p.slug}>${p.name}</option>`,
                        )}
                    </select>
                    <button
                        class="btn btn-sm btn-primary"
                        ?disabled=${this._settingState}
                        @click=${() => this._setProduct(this._triageProduct)}
                    >
                        Assign
                    </button>
                </div>
            </div>
        `;
    }

    _renderComment(comment) {
        const fromTeam = comment.origin === 'github';
        // Only `agent` is surfaced. `web` and `cli` are both a person typing,
        // and labelling those would turn a meaningful signal into decoration
        // nobody reads. An absent client says nothing either way — most of the
        // thread predates the field.
        const byAgent = comment.client === 'agent';
        return html`
            <div class="list-group-item">
                <div class="d-flex align-items-center gap-2 mb-1">
                    <span class="fw-semibold small">${comment.authorHandle}</span>
                    ${fromTeam ? html`<span class="badge text-bg-primary">D3</span>` : nothing}
                    ${
                        byAgent
                            ? html`<span
                                  class="badge text-bg-light border text-muted fw-normal"
                                  title="Written by an AI assistant acting for ${comment.authorHandle}, not typed by them"
                                  ><i class="bi bi-robot me-1"></i>via assistant</span
                              >`
                            : nothing
                    }
                    <span class="text-muted small">${formatItemDate(comment.createdAt)}</span>
                </div>
                <div class="markdown-body">${renderMarkdown(comment.body)}</div>
            </div>
        `;
    }

    render() {
        if (this._loading) {
            return html`
                <div class="text-muted small d-flex align-items-center gap-2">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                    Loading…
                </div>
            `;
        }

        if (this._error && !this._item) {
            return html`
                <div class="alert alert-warning" role="alert">${this._error}</div>
                <button class="btn btn-link px-0" @click=${() => router.navigate('/')}>
                    <i class="bi bi-arrow-left me-1"></i>Back
                </button>
            `;
        }

        const item = this._item;
        const type = itemTypeMeta(item.type);
        const closed = item.state === 'closed';
        const archived = item.archived === true;

        return html`
            <button class="btn btn-link px-0 mb-2" @click=${() => router.navigate('/')}>
                <i class="bi bi-arrow-left me-1"></i>Back
            </button>

            <div class="card shadow-sm border-0 mb-4">
                <div class="card-body p-4">
                    <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                        <i class="bi ${type.icon} text-secondary"></i>
                        <span class="text-muted small">${type.label}</span>
                        ${
                            closed
                                ? html`<span class="badge text-bg-secondary">Closed</span>`
                                : html`<span class="badge text-bg-success">Open</span>`
                        }
                        ${
                            archived
                                ? html`<span class="badge text-bg-light border">Archived</span>`
                                : nothing
                        }
                        ${this._renderTriageBadge(PRIORITIES, item.priority, 'Priority')}
                        ${this._renderTriageBadge(SEVERITIES, item.severity, 'Severity')}
                        <div class="d-flex gap-2 ms-auto">
                            <button
                                class="btn btn-sm ${this._following ? 'btn-outline-secondary' : 'btn-outline-primary'}"
                                ?disabled=${this._settingFollow}
                                title=${
                                    this._following
                                        ? "You'll hear about replies and status changes"
                                        : 'Hear about replies and status changes'
                                }
                                @click=${() => this._setFollowing(!this._following)}
                            >
                                <i class="bi ${this._following ? 'bi-bell-slash' : 'bi-bell'} me-1"></i>${
                                    this._following ? 'Unfollow' : 'Follow'
                                }
                            </button>
                        ${
                            this.isStaff
                                ? html`
                                      <button
                                          class="btn btn-sm btn-outline-secondary"
                                          ?disabled=${this._settingState}
                                          @click=${() => this._setState(closed ? 'open' : 'closed')}
                                      >
                                          ${closed ? 'Reopen' : 'Close'}
                                      </button>
                                      ${
                                          closed || archived
                                              ? html`<button
                                                    class="btn btn-sm btn-outline-secondary"
                                                    ?disabled=${this._settingState}
                                                    @click=${() => this._setArchived(!archived)}
                                                >
                                                    ${archived ? 'Unarchive' : 'Archive'}
                                                </button>`
                                              : nothing
                                      }
                                  `
                                : nothing
                        }
                        </div>
                    </div>
                    ${this._editing ? this._renderEditor() : this._renderBody(item)}
                    ${this.isStaff ? this._renderStaffPanel(item) : nothing}
                </div>
            </div>

            <h2 class="h5 mb-3">
                ${this._comments.length === 0 ? 'No replies yet' : 'Replies'}
            </h2>
            ${
                this._comments.length > 0
                    ? html`<div class="list-group shadow-sm mb-3">
                          ${this._comments.map((c) => this._renderComment(c))}
                      </div>`
                    : nothing
            }

            <form @submit=${this._comment}>
                <community-markdown-input
                    class="d-block mb-2"
                    input-id="comment-body"
                    rows="3"
                    placeholder="Add a reply"
                    .value=${this._draft}
                    @value-changed=${(e) => {
                        this._draft = e.detail.value;
                    }}
                    @uploading-changed=${(e) => {
                        this._uploadingComment = e.detail.uploading;
                    }}
                ></community-markdown-input>
                ${
                    this._error && this._item
                        ? html`<div class="alert alert-danger py-2">${this._error}</div>`
                        : nothing
                }
                <button
                    class="btn btn-primary"
                    type="submit"
                    ?disabled=${this._sending || this._uploadingComment || this._draft.trim() === ''}
                >
                    ${
                        this._sending
                            ? html`<span
                                      class="spinner-border spinner-border-sm me-2"
                                      role="status"
                                  ></span>Sending…`
                            : html`<i class="bi bi-reply me-1"></i>Reply`
                    }
                </button>
            </form>
        `;
    }
}

customElements.define('community-item-page', CommunityItemPage);
